import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Car, CurrencyCircleDollar, GoogleLogo, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const FX_CURRENCIES = [
    { code: "USD", label: "Dólar estadounidense", symbol: "$" },
    { code: "GBP", label: "Libra esterlina", symbol: "£" },
    { code: "MXN", label: "Peso mexicano", symbol: "$" },
];

export default function AdminSettings() {
    const [kmRate, setKmRate] = useState("");
    const [fxRates, setFxRates] = useState({ USD: "", GBP: "", MXN: "" });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingFx, setSavingFx] = useState(false);
    const [sheet, setSheet] = useState(null);
    const [sheetBusy, setSheetBusy] = useState(false);
    const [folderUrl, setFolderUrl] = useState("");

    const loadSheet = () =>
        http.get("/sheets/status").then((r) => setSheet(r.data));

    useEffect(() => {
        http.get("/settings")
            .then((r) => {
                setKmRate(String(r.data.km_rate ?? ""));
                const fx = r.data.fx_rates || {};
                setFxRates({
                    USD: String(fx.USD ?? ""),
                    GBP: String(fx.GBP ?? ""),
                    MXN: String(fx.MXN ?? ""),
                });
            })
            .finally(() => setLoading(false));
        loadSheet();
    }, []);

    const setupSheet = async () => {
        setSheetBusy(true);
        try {
            const r = await http.post("/sheets/setup", {
                folder_url: folderUrl.trim() || null,
            });
            toast.success("Hoja creada. Sincronización activada.");
            setSheet((s) => ({ ...(s || {}), ...r.data }));
            loadSheet();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo crear la hoja");
        } finally {
            setSheetBusy(false);
        }
    };

    const saveKm = async (e) => {
        e.preventDefault();
        const rate = Number(kmRate);
        if (Number.isNaN(rate) || rate < 0) {
            toast.error("Introduce una tarifa válida");
            return;
        }
        setSaving(true);
        try {
            const r = await http.put("/settings", { km_rate: rate });
            setKmRate(String(r.data.km_rate));
            toast.success("Ajustes guardados");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    };

    const saveFx = async (e) => {
        e.preventDefault();
        const rates = {};
        for (const c of FX_CURRENCIES) {
            const v = Number(fxRates[c.code]);
            if (fxRates[c.code] !== "" && (Number.isNaN(v) || v <= 0)) {
                toast.error(`Tipo de cambio inválido para ${c.code}`);
                return;
            }
            if (fxRates[c.code] !== "") rates[c.code] = v;
        }
        setSavingFx(true);
        try {
            const r = await http.put("/settings", {
                km_rate: Number(kmRate) || 0,
                fx_rates: rates,
            });
            const fx = r.data.fx_rates || {};
            setFxRates({
                USD: String(fx.USD ?? ""),
                GBP: String(fx.GBP ?? ""),
                MXN: String(fx.MXN ?? ""),
            });
            toast.success("Tipos de cambio guardados");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setSavingFx(false);
        }
    };

    return (
        <div data-testid="page-admin-settings">
            <PageHeader
                eyebrow="Administración"
                title="Ajustes"
                description="Configuración general de la aplicación."
            />
            <div className="p-6 lg:p-10 max-w-2xl space-y-6">
                <div className="border border-slate-200 bg-white">
                    <div className="px-6 py-5 border-b border-slate-200 flex items-center gap-2">
                        <Car size={18} className="text-[#002BAF]" />
                        <div className="font-display text-lg font-semibold">
                            Gastos de desplazamiento
                        </div>
                    </div>
                    <form onSubmit={saveKm} className="px-6 py-6 space-y-4">
                        <div className="max-w-xs">
                            <Label className="text-xs">Tarifa por kilómetro (€/km)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={kmRate}
                                onChange={(e) => setKmRate(e.target.value)}
                                disabled={loading}
                                className="mt-1 rounded-sm border-slate-300 font-mono"
                                placeholder="0.45"
                                data-testid="settings-km-rate"
                            />
                            <p className="text-[11px] text-slate-500 mt-1.5">
                                Importe pagado por cada km en desplazamientos en coche. Se aplica
                                automáticamente al registrar un gasto de tipo "Desplazamiento".
                            </p>
                        </div>
                        <Button
                            type="submit"
                            disabled={saving || loading}
                            className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                            data-testid="settings-save"
                        >
                            {saving ? "Guardando…" : "Guardar cambios"}
                        </Button>
                    </form>
                </div>

                <div className="border border-slate-200 bg-white">
                    <div className="px-6 py-5 border-b border-slate-200 flex items-center gap-2">
                        <CurrencyCircleDollar size={18} className="text-[#002BAF]" />
                        <div className="font-display text-lg font-semibold">
                            Tipos de cambio a euros
                        </div>
                    </div>
                    <form onSubmit={saveFx} className="px-6 py-6 space-y-4">
                        <p className="text-[11px] text-slate-500">
                            Valor en euros de 1 unidad de cada moneda. Se usan para consolidar en
                            euros las facturas soportadas importadas y como valor por defecto al
                            registrar una factura en otra moneda (editable por factura).
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {FX_CURRENCIES.map((c) => (
                                <div key={c.code}>
                                    <Label className="text-xs">
                                        1 {c.code} ({c.symbol}) = € {" "}
                                    </Label>
                                    <Input
                                        type="number"
                                        step="0.0001"
                                        min="0"
                                        value={fxRates[c.code]}
                                        onChange={(e) =>
                                            setFxRates((f) => ({
                                                ...f,
                                                [c.code]: e.target.value,
                                            }))
                                        }
                                        disabled={loading}
                                        className="mt-1 rounded-sm border-slate-300 font-mono"
                                        data-testid={`settings-fx-${c.code}`}
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1">{c.label}</p>
                                </div>
                            ))}
                        </div>
                        <Button
                            type="submit"
                            disabled={savingFx || loading}
                            className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                            data-testid="settings-fx-save"
                        >
                            {savingFx ? "Guardando…" : "Guardar tipos de cambio"}
                        </Button>
                    </form>
                </div>

                <div className="border border-slate-200 bg-white">
                    <div className="px-6 py-5 border-b border-slate-200 flex items-center gap-2">
                        <GoogleLogo size={18} className="text-[#002BAF]" weight="bold" />
                        <div className="font-display text-lg font-semibold">
                            Sincronización con Google Sheets
                        </div>
                    </div>
                    <div className="px-6 py-6 space-y-4">
                        <p className="text-[11px] text-slate-500">
                            Mantiene una hoja de Google con TODAS las facturas soportadas
                            (una pestaña por año) actualizada automáticamente al crear, editar,
                            borrar, cambiar el estado o importar facturas. Compártela con tu
                            asesoría en modo lectura.
                        </p>

                        {sheet && !sheet.drive_connected && (
                            <div
                                className="border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3 rounded-sm"
                                data-testid="sheets-need-drive"
                            >
                                Primero conecta Google en{" "}
                                <span className="font-semibold">
                                    Administración → Gastos y dietas
                                </span>{" "}
                                (botón "Conectar Google Drive"). La misma conexión sirve para
                                las hojas de cálculo.
                            </div>
                        )}

                        {sheet && sheet.drive_connected && !sheet.spreadsheet_url && (
                            <div className="space-y-3">
                                <div className="text-xs text-slate-600">
                                    Conectado como{" "}
                                    <span className="font-medium">
                                        {sheet.connected_email || "cuenta de Google"}
                                    </span>
                                    . Asegúrate de haber habilitado la API de Google Sheets en
                                    tu proyecto de Google Cloud.
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        Carpeta de Google Drive (opcional)
                                    </Label>
                                    <Input
                                        value={folderUrl}
                                        onChange={(e) => setFolderUrl(e.target.value)}
                                        placeholder="https://drive.google.com/drive/folders/…"
                                        className="mt-1 rounded-sm border-slate-300 text-sm"
                                        data-testid="sheets-folder-url"
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1">
                                        Pega el enlace de la carpeta donde quieres crear la hoja.
                                        La cuenta conectada debe tener acceso de edición. Si lo
                                        dejas vacío, se creará en "Mi unidad".
                                    </p>
                                </div>
                                <Button
                                    onClick={setupSheet}
                                    disabled={sheetBusy}
                                    className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                    data-testid="sheets-setup-btn"
                                >
                                    {sheetBusy ? "Creando…" : "Crear hoja y activar sincronización"}
                                </Button>
                            </div>
                        )}

                        {sheet && sheet.spreadsheet_url && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm text-emerald-700">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                    Sincronización activa
                                    {sheet.last_sync && (
                                        <span className="text-slate-400 text-xs">
                                            · última:{" "}
                                            {new Date(sheet.last_sync).toLocaleString("es-ES")}
                                        </span>
                                    )}
                                </div>
                                <a
                                    href={sheet.spreadsheet_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 text-sm font-medium text-[#002BAF] hover:underline"
                                    data-testid="sheets-open-link"
                                >
                                    <ArrowSquareOut size={15} /> Abrir la hoja de Google
                                </a>
                                <p className="text-[11px] text-slate-500">
                                    Comparte esta hoja con tu asesoría (botón "Compartir" en
                                    Google Sheets, acceso de solo lectura). Se actualiza sola con
                                    cada cambio en las facturas.
                                </p>
                                {sheet.last_error && (
                                    <div className="text-[11px] text-red-600">
                                        Último error de sincronización: {sheet.last_error}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
